import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BassitLogo } from '@/components/BassitLogo';
import { BassitWordmark } from '@/components/BassitWordmark';
import { LangToggle } from '@/components/LangToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Arrow, Spinner } from '@/components/icons';
import { useThemeAndLocale } from '@/hooks/useThemeAndLocale';
import { LOGIN_I18N } from '@/i18n/loginStrings';

/**
 * Login — Direction A · Quiet Authority
 *
 * Used when the host is a tenant / console / admin subdomain. Single narrow
 * column, generous whitespace, no editorial aside. Same auth flow as
 * LoginPage.tsx (Charter / B).
 */

const REQUEST_TIMEOUT_MS = 30_000;

const normalizeUrl = (v?: string): string =>
  String(v || '').trim().replace(/\/+$/, '');

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const runtime = (window as any).__RUNTIME_CONFIG__ as Record<string, unknown> | undefined;
    const runtimeBase = normalizeUrl(String(runtime?.API_BASE_URL || runtime?.apiBaseUrl || ''));
    if (runtimeBase) return runtimeBase.endsWith('/api') ? runtimeBase : `${runtimeBase}/api`;
    const host = String(window.location.hostname || '').toLowerCase();
    const protocol = window.location.protocol || 'https:';
    const port = String(window.location.port || '').trim();
    const portSuffix = port ? `:${port}` : '';
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
      return `${protocol}//api.localhost${portSuffix}/api`;
    }
    const parts = host.split('.').filter(Boolean);
    if (parts.length >= 2) {
      const base = parts.slice(-2).join('.');
      return `${protocol}//api.${base}${portSuffix}/api`;
    }
  }
  const envBase = normalizeUrl(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL);
  if (envBase) return envBase.endsWith('/api') ? envBase : `${envBase}/api`;
  return '/api';
};

const resolveReturnTo = (raw: string | null): string | undefined => {
  const v = String(raw || '').trim();
  if (!v) return undefined;
  if (v.startsWith('/') && !v.startsWith('//')) return v;
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(v)) return `https://${v}`;
  return undefined;
};

const fetchWithTimeout = async (url: string, init: RequestInit, ms: number) => {
  const c = new AbortController();
  const id = window.setTimeout(() => c.abort(), ms);
  try { return await fetch(url, { ...init, signal: c.signal }); }
  finally { window.clearTimeout(id); }
};

export default function LoginPageQuiet() {
  const { locale, setLocale, theme, setTheme } = useThemeAndLocale();
  const t = LOGIN_I18N[locale];
  const rtl = locale === 'ar';

  const location = useLocation();
  const returnTo = useMemo(
    () => resolveReturnTo(new URLSearchParams(location.search).get('returnTo')),
    [location.search]
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoState, setLogoState] = useState<'intro' | 'converge' | ''>('intro');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setLogoState(''), 1300);
    return () => window.clearTimeout(id);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    setLogoState('converge');
    try {
      const res = await fetchWithTimeout(
        `${getApiBaseUrl()}/auth/discover`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email: email.trim(), password, ...(returnTo ? { returnTo } : {}) }),
        },
        REQUEST_TIMEOUT_MS
      );
      const payload = await res.json().catch(() => ({}));
      const data = payload?.data ?? payload;
      if (!res.ok || payload?.success === false) {
        setError(String(payload?.error?.message || payload?.message || 'Authentication temporarily unavailable.'));
        return;
      }
      if (data?.redirectUrl) { window.location.replace(String(data.redirectUrl)); return; }
      setError('Login succeeded but no destination was returned.');
    } catch (err: any) {
      setError(err?.name === 'AbortError' ? 'Authentication timed out.' : err?.message || 'Network error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      className="relative min-h-screen w-full grid grid-rows-[auto_1fr_auto] overflow-hidden bg-app text-emphasis"
    >
      {/* ambient radial glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            theme === 'dark'
              ? 'radial-gradient(60% 40% at 50% 0%, oklch(0.22 0.04 50 / 0.5), transparent 70%), radial-gradient(40% 30% at 50% 100%, oklch(0.20 0.03 50 / 0.4), transparent 70%)'
              : 'radial-gradient(60% 40% at 50% 0%, oklch(0.93 0.02 50 / 0.6), transparent 70%)',
        }}
      />

      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-7">
        <div className="flex items-center gap-3">
          <BassitLogo
            size={28}
            intro={logoState === 'intro'}
            converge={logoState === 'converge'}
            pulse={loading}
          />
          <BassitWordmark variant={rtl ? 'ar' : 'en'} size={16} />
        </div>
        <div className="flex items-center gap-3">
          <LangToggle locale={locale} onChange={setLocale} />
          <ThemeToggle theme={theme} onChange={setTheme} />
        </div>
      </header>

      <main className="relative z-10 flex items-center justify-center px-6 sm:px-10">
        <div className="w-full max-w-[380px] flex flex-col gap-7">
          <div className="mono" style={{ color: 'var(--brand)' }}>{t.eyebrowA}</div>
          <h1
            className="display m-0"
            style={{ fontSize: 'clamp(40px, 7vw, 56px)' }}
          >
            {t.welcomeA[0]}<em>{t.welcomeA[1]}</em>{t.welcomeA[2]}
          </h1>
          <p className="m-0 text-[16px] leading-[1.5] max-w-[340px]" style={{ color: 'var(--secondary)' }}>
            {t.subA}
          </p>

          {error && (
            <div
              role="alert"
              className="rounded-md text-[13px] px-3 py-2.5"
              style={{
                background: 'rgba(232, 70, 58, 0.08)',
                border: '1px solid rgba(232, 70, 58, 0.4)',
                color: '#f4b6a8',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-[22px] mt-2">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="qa-email"
                className="font-mono uppercase text-[11px] tracking-[0.12em]"
                style={{ color: 'var(--muted)' }}
              >
                {t.email}
              </label>
              <input
                id="qa-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPlaceA}
                required
                disabled={loading}
                autoFocus
                className="bg-transparent outline-none text-[16px] py-[10px] border-0 transition-colors"
                style={{
                  borderBottom: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
                onFocus={(e) => (e.currentTarget.style.borderBottomColor = 'var(--brand)')}
                onBlur={(e) => (e.currentTarget.style.borderBottomColor = 'var(--border)')}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <label
                  htmlFor="qa-pw"
                  className="font-mono uppercase text-[11px] tracking-[0.12em]"
                  style={{ color: 'var(--muted)' }}
                >
                  {t.password}
                </label>
                <a
                  href="#"
                  className="mono transition-colors"
                  style={{ color: 'var(--secondary)' }}
                >
                  {t.forgot}
                </a>
              </div>
              <input
                id="qa-pw"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.pwPlaceA}
                required
                disabled={loading}
                className="bg-transparent outline-none text-[16px] py-[10px] border-0 transition-colors"
                style={{ borderBottom: '1px solid var(--border)', color: 'var(--text)' }}
                onFocus={(e) => (e.currentTarget.style.borderBottomColor = 'var(--brand)')}
                onBlur={(e) => (e.currentTarget.style.borderBottomColor = 'var(--border)')}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md text-[14px] font-medium tracking-[-0.005em] py-[13px] transition-all disabled:opacity-80"
              style={{
                background: 'var(--brand)',
                color: '#1a0d05',
                border: '1px solid var(--brand)',
              }}
            >
              {loading ? <Spinner /> : (<>{t.signIn} <Arrow rtl={rtl} /></>)}
            </button>
          </form>

          <div className="text-[13px] pt-[18px] border-t border-soft">
            <span className="mono text-muted">{t.needAccess}</span>{' '}
            <a
              href="#"
              className="transition-colors"
              style={{
                color: 'var(--text)',
                borderBottom: '1px solid var(--border)',
                paddingBottom: 1,
              }}
            >
              {t.contactAdmin}
            </a>
          </div>
        </div>
      </main>

      <footer className="relative z-10 flex flex-wrap items-center justify-between gap-2 px-6 sm:px-10 py-7 text-[11.5px]">
        <span className="mono text-muted">© 2026 BassitAI</span>
        <div className="flex items-center gap-3.5">
          <span className="mono text-muted">{t.bottomCity}</span>
          <span
            className="inline-block w-[3px] h-[3px] rounded-full"
            style={{ background: 'var(--muted)' }}
          />
          <span className="mono text-muted">{t.bottomCert}</span>
        </div>
      </footer>
    </div>
  );
}
